import { useState } from 'react';
import { toolRegistry, ToolCategory, ToolMeta } from '../shared/data/toolRegistry';
import { ToolCard } from '../shared/components/ToolCard';
import { Zap, Laptop, BookOpen, Filter } from 'lucide-react';

interface HomeProps {
  searchQuery: string;
}

export function Home({ searchQuery }: HomeProps) {
  const [selectedCategory, setSelectedCategory] = useState<ToolCategory | 'all'>('all');

  const categories: { id: ToolCategory | 'all'; name: string }[] = [
    { id: 'all', name: 'All Tools' },
    { id: 'microeconomics', name: 'Microeconomics' },
    { id: 'macroeconomics', name: 'Macroeconomics' },
    { id: 'data', name: 'Data & Policy' },
    { id: 'finance', name: 'Finance & Decision' }
  ];

  // Filtering logic
  const filteredTools = toolRegistry.filter((tool: ToolMeta) => {
    const matchesCategory = selectedCategory === 'all' || tool.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.shortTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tool.keywords.some((k) => k.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  return (
    <div className="flex-1 w-full max-w-7xl mx-auto px-4 py-8 md:px-6 md:py-12 space-y-12">
      {/* Hero Section */}
      <section className="text-center max-w-3xl mx-auto space-y-4">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold font-display tracking-tight text-slate-800 dark:text-slate-100">
          Econ<span className="text-brand-500">Dock</span>
        </h1>
        <p className="text-lg md:text-xl text-slate-500 dark:text-slate-400 font-medium">
          Fast economics calculators, charts, and data tools for students.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-3">
          <button
            onClick={() => setSelectedCategory('microeconomics')}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-indigo-50 hover:bg-indigo-100 border border-indigo-100 text-indigo-700 dark:bg-indigo-950/20 dark:hover:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-900/30 transition"
          >
            Start with Microeconomics
          </button>
          <button
            onClick={() => setSelectedCategory('macroeconomics')}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-50 hover:bg-emerald-100 border border-emerald-100 text-emerald-700 dark:bg-emerald-950/20 dark:hover:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/30 transition"
          >
            Explore Macro Tools
          </button>
          <button
            onClick={() => setSelectedCategory('data')}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-100 text-amber-700 dark:bg-amber-950/20 dark:hover:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30 transition"
          >
            Open Data Tools
          </button>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="space-y-6">
        <div className="flex items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-3 flex-wrap">
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold">
            <Filter className="w-4 h-4 text-brand-500" />
            <span>Categories</span>
          </div>

          <div className="flex flex-wrap gap-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-3.5 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  selectedCategory === cat.id
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Tools Grid */}
        {filteredTools.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredTools.map((tool) => (
              <ToolCard key={tool.id} tool={tool} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-slate-200/60 dark:border-slate-800/80">
            <p className="text-slate-400 dark:text-slate-500 font-medium">
              No calculators match your search query. Try another term or reset search query.
            </p>
          </div>
        )}
      </section>

      {/* Why EconDock Section */}
      <section className="glass-panel p-6 md:p-8 space-y-6">
        <div className="text-center max-w-xl mx-auto space-y-2">
          <h2 className="text-2xl font-bold font-display text-slate-800 dark:text-slate-200">
            Why EconDock?
          </h2>
          <p className="text-sm text-slate-400 dark:text-slate-500 font-medium">
            A privacy-centric educational toolkit built for homework and quick analysis.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950/20 text-brand-500 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">
                Local-First & Fast
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                No slow server roundtrips, no loading screens. Every calculation happens instantly in your browser. Works fully offline.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950/20 text-brand-500 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">
                Formula-First Explanations
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                We substitute your inputs directly into standard economic formulas so you can trace every step and learn how it is computed.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950/20 text-brand-500 flex items-center justify-center flex-shrink-0">
              <Laptop className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200 mb-1">
                Zero AI Dependability
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                Deterministic engines mean we never hallucinate equations, negative values, or logical flows. Reliable results, every time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
